import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { usePermissions } from '@/hooks/usePermissions';
import { isListItemCompletedForCalendarLock } from '@/hooks/useCompletedJobEditLock';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import type { ConvertJobToEstimateResultDto } from '@/types';

interface Props {
  job: { id: string; status?: string; payment_status?: string | null; company?: { id: string } | null };
  onClose: () => void;
  /** jobDeleted is false when the user kept the job, or chose delete but the delete failed. */
  onDone: (result: ConvertJobToEstimateResultDto, jobDeleted: boolean) => void;
}

/**
 * "Create Estimate" confirmation: copies the job's items and contacts into a new estimate, then
 * optionally deletes the job. Creating and deleting are separate calls so deleting stays gated by
 * jobs.delete at the gateway.
 */
const BUTTON_SIZE = 'flex-1 sm:flex-none whitespace-nowrap px-3 sm:px-4 py-2 text-sm';

export default function CreateEstimateFromJobDialog({ job, onClose, onDone }: Props) {
  const { addNotification } = useNotifications();
  const { isFeatureAllowed } = usePermissions();
  // Which button is working, so only that one shows the spinner text.
  const [busy, setBusy] = useState<'keep' | 'delete' | null>(null);
  // Only matters for "Keep job": a replaced job's attachments are always carried over (server side too).
  const [copyAttachments, setCopyAttachments] = useState(true);
  // backNavigation off: on success the caller closes/opens modals itself, and a history pop here would race it.
  const layer = useModalLayer({ enabled: true, onClose, backNavigation: false });

  const companyId = job.company?.id;
  const { data: jobStatuses } = useQuery({
    queryKey: ['jobStatuses', companyId],
    queryFn: () => apiService.getJobStatusesByCompany(companyId!),
    enabled: !!companyId,
  });

  // Same rules the server enforces for replacing_job; shown up front instead of as an error.
  const deleteBlockedReason =
    job.payment_status && job.payment_status !== 'NotPaid'
      ? 'Jobs with payments can’t be deleted.'
      : isListItemCompletedForCalendarLock(job, jobStatuses)
        ? 'Completed jobs can’t be deleted.'
        : null;
  const canDelete = isFeatureAllowed('jobs.delete');

  const handleCreate = async (willDelete: boolean) => {
    setBusy(willDelete ? 'delete' : 'keep');
    try {
      const result = await apiService.createEstimateFromJob(job.id, { replacing_job: willDelete, copy_attachments: copyAttachments });
      let jobDeleted = false;
      if (willDelete) {
        try {
          await apiService.deleteJob(job.id);
          jobDeleted = true;
        } catch (error: any) {
          const details = error.response?.data?.details?.join?.(' ') || error.response?.data?.message || '';
          addNotification(notificationService.error(`Estimate created, but the job couldn’t be deleted. ${details}`.trim()));
        }
      }
      if (jobDeleted || !willDelete) addNotification(notificationService.success(result.message));
      onDone(result, jobDeleted);
    } catch (error: any) {
      addNotification(notificationService.error(error.response?.data?.message || 'Failed to create estimate'));
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={layer.style} role="dialog" aria-modal="true">
        <div className="bg-white rounded-lg p-5 sm:p-6 max-w-md w-full mx-4 shadow-xl">
          <h3 className="text-lg font-semibold mb-2">Create Estimate?</h3>
          <p className="text-gray-600 mb-2">
            A new estimate will be created with this job’s items and contacts. Keep the job to link the
            estimate to it, or delete the job to replace it.
          </p>
          <label className="flex items-start gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={copyAttachments}
              onChange={(e) => setCopyAttachments(e.target.checked)}
              disabled={!!busy}
            />
            <span>
              Copy photos &amp; files
              {canDelete && <span className="block text-xs text-gray-500">Delete job always keeps them.</span>}
            </span>
          </label>
          {canDelete && (
            <p className="text-amber-700 text-sm mb-4">
              {deleteBlockedReason ?? (
                <>
                  <strong>Delete job</strong> permanently deletes the job and its invoice.
                </>
              )}
            </p>
          )}
          {/* One row on phones too: buttons share the width there, natural width from sm up. */}
          <div className="flex justify-end gap-2 sm:gap-3 mt-4">
            <button onClick={onClose} className={`btn-secondary ${BUTTON_SIZE}`} disabled={!!busy}>
              Cancel
            </button>
            {canDelete && (
              <button
                onClick={() => handleCreate(true)}
                disabled={!!busy || !!deleteBlockedReason}
                title={deleteBlockedReason ?? undefined}
                className={`${BUTTON_SIZE} bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {busy === 'delete' ? 'Creating...' : 'Delete job'}
              </button>
            )}
            <button
              onClick={() => handleCreate(false)}
              disabled={!!busy}
              className={`${BUTTON_SIZE} bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50`}
            >
              {busy === 'keep' ? 'Creating...' : 'Keep job'}
            </button>
          </div>
        </div>
      </div>
    </LayerDepthContext.Provider>,
    document.body,
  );
}
